import React, { useState } from 'react';
import { Mail, Eye, X, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { format } from 'date-fns';

const API = '/api';

const EmailLogsTab = () => {
  const [emailLogs, setEmailLogs] = useState([]);
  const [emailSearchQuery, setEmailSearchQuery] = useState('');
  const [resendingEmail, setResendingEmail] = useState(null);
  const [viewingEmail, setViewingEmail] = useState(null);
  const [emailContent, setEmailContent] = useState('');

  const fetchEmailLogs = async (searchEmail = '') => {
    const token = localStorage.getItem('admin_token');
    try {
      const url = searchEmail 
        ? `${API}/emails/logs?email=${encodeURIComponent(searchEmail)}&limit=100`
        : `${API}/emails/logs?limit=100`;
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmailLogs(response.data.logs || []);
    } catch (error) {
      console.error('Failed to fetch email logs:', error);
    }
  };

  const handleResendEmail = async (email, emailType) => {
    const token = localStorage.getItem('admin_token');
    setResendingEmail(`${email}-${emailType}`);
    try {
      await axios.post(`${API}/emails/resend?email=${encodeURIComponent(email)}&email_type=${emailType}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Email resent to ${email}`);
      fetchEmailLogs(emailSearchQuery);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resend email');
    } finally {
      setResendingEmail(null);
    }
  };

  const handleViewEmail = (log) => {
    if (log.html_content) {
      setEmailContent(log.html_content);
      setViewingEmail(log);
    } else {
      toast.error('Email content not available for this log');
    }
  };

  const closeEmailViewer = () => {
    setViewingEmail(null);
    setEmailContent('');
  };

  return (
    <div>
      <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
        <Mail className="w-5 h-5 text-[#FF4500]" />
        Email Logs & Resend
      </h3>
      
      {/* Email Viewer Modal */}
      {viewingEmail && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="bg-gray-100 px-6 py-4 border-b flex items-center justify-between">
              <div>
                <h4 className="font-bold text-lg">{viewingEmail.subject}</h4>
                <p className="text-sm text-gray-600">
                  To: {viewingEmail.to} • {viewingEmail.email_type} • {format(new Date(viewingEmail.sent_at), 'MMM d, yyyy h:mm a')}
                </p>
              </div>
              <button
                onClick={closeEmailViewer}
                className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            {/* Modal Body - Email Content */}
            <div className="flex-1 overflow-auto p-4 bg-gray-50">
              <div 
                className="bg-white rounded-lg shadow-sm border border-gray-200"
                dangerouslySetInnerHTML={{ __html: emailContent }}
              />
            </div>
          </div>
        </div>
      )}
      
      {/* Search by email */}
      <div className="flex gap-4 mb-6">
        <input
          type="email"
          placeholder="Search by email address..."
          value={emailSearchQuery}
          onChange={(e) => setEmailSearchQuery(e.target.value)}
          className="flex-1 h-12 px-4 bg-[#F4F4F5] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF4500]"
        />
        <button
          onClick={() => fetchEmailLogs(emailSearchQuery)}
          className="px-6 py-2 bg-[#FF4500] text-white rounded-lg hover:bg-[#E03E00] transition-all font-semibold"
        >
          Search
        </button>
        <button
          onClick={() => { setEmailSearchQuery(''); fetchEmailLogs(''); }}
          className="px-6 py-2 bg-gray-200 text-[#0A0A0A] rounded-lg hover:bg-gray-300 transition-all font-semibold"
        >
          Clear
        </button>
      </div>

      {/* Resend Email Quick Actions */}
      {emailSearchQuery && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <h4 className="font-semibold text-blue-800 mb-3">Quick Resend for: {emailSearchQuery}</h4>
          
          {/* Row 1: Welcome & Appointment */}
          <p className="text-xs text-blue-600 mb-2 font-semibold">Registration & Booking:</p>
          <div className="flex flex-wrap gap-2 mb-4">
            <button
              onClick={() => handleResendEmail(emailSearchQuery, 'welcome_guide')}
              disabled={resendingEmail === `${emailSearchQuery}-welcome_guide`}
              className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all font-semibold disabled:opacity-50 text-xs"
            >
              {resendingEmail === `${emailSearchQuery}-welcome_guide` ? '...' : 'Welcome Guide'}
            </button>
            <button
              onClick={() => handleResendEmail(emailSearchQuery, 'appointment_confirmation')}
              disabled={resendingEmail === `${emailSearchQuery}-appointment_confirmation`}
              className="px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-all font-semibold disabled:opacity-50 text-xs"
            >
              {resendingEmail === `${emailSearchQuery}-appointment_confirmation` ? '...' : 'Appointment Confirmation'}
            </button>
          </div>

          {/* Row 2: Post-Call & Payment Chase */}
          <p className="text-xs text-green-600 mb-2 font-semibold">Post-Call & Payment:</p>
          <div className="flex flex-wrap gap-2 mb-4">
            <button
              onClick={() => handleResendEmail(emailSearchQuery, 'post_call')}
              disabled={resendingEmail === `${emailSearchQuery}-post_call`}
              className="px-3 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-all font-semibold disabled:opacity-50 text-xs"
            >
              {resendingEmail === `${emailSearchQuery}-post_call` ? '...' : 'Post-Call + Payment Link'}
            </button>
            <button
              onClick={() => handleResendEmail(emailSearchQuery, 'payment_chase_day3')}
              disabled={resendingEmail === `${emailSearchQuery}-payment_chase_day3`}
              className="px-3 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-all font-semibold disabled:opacity-50 text-xs"
            >
              {resendingEmail === `${emailSearchQuery}-payment_chase_day3` ? '...' : 'Day 3 Payment Reminder'}
            </button>
            <button
              onClick={() => handleResendEmail(emailSearchQuery, 'payment_chase_day7')}
              disabled={resendingEmail === `${emailSearchQuery}-payment_chase_day7`}
              className="px-3 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-all font-semibold disabled:opacity-50 text-xs"
            >
              {resendingEmail === `${emailSearchQuery}-payment_chase_day7` ? '...' : 'Day 7 Final Reminder'}
            </button>
          </div>

          {/* Row 3: Refund Countdown */}
          <p className="text-xs text-amber-600 mb-2 font-semibold">Refund Countdown (for paid leads):</p>
          <div className="flex flex-wrap gap-2 mb-4">
            <button
              onClick={() => handleResendEmail(emailSearchQuery, 'countdown_15')}
              disabled={resendingEmail === `${emailSearchQuery}-countdown_15`}
              className="px-3 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-all font-semibold disabled:opacity-50 text-xs"
            >
              {resendingEmail === `${emailSearchQuery}-countdown_15` ? '...' : '15-Day Countdown'}
            </button>
            <button
              onClick={() => handleResendEmail(emailSearchQuery, 'countdown_30')}
              disabled={resendingEmail === `${emailSearchQuery}-countdown_30`}
              className="px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-all font-semibold disabled:opacity-50 text-xs"
            >
              {resendingEmail === `${emailSearchQuery}-countdown_30` ? '...' : '30-Day Deadline'}
            </button>
          </div>

          {/* Row 4: Chase Emails */}
          <p className="text-xs text-gray-600 mb-2 font-semibold">Lead Chase Emails:</p>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => handleResendEmail(emailSearchQuery, 'chase_no_booking')}
              disabled={resendingEmail === `${emailSearchQuery}-chase_no_booking`}
              className="px-3 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-all font-semibold disabled:opacity-50 text-xs"
            >
              {resendingEmail === `${emailSearchQuery}-chase_no_booking` ? '...' : 'Chase: Book a Call'}
            </button>
            <button
              onClick={() => handleResendEmail(emailSearchQuery, 'chase_no_action')}
              disabled={resendingEmail === `${emailSearchQuery}-chase_no_action`}
              className="px-3 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-all font-semibold disabled:opacity-50 text-xs"
            >
              {resendingEmail === `${emailSearchQuery}-chase_no_action` ? '...' : 'Chase: No Action'}
            </button>
          </div>
        </div>
      )}

      {/* Test Email Section */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
        <h4 className="font-semibold text-yellow-800 mb-3">Test Email Configuration</h4>
        <p className="text-sm text-yellow-700 mb-3">Send a test email to verify SendGrid is working correctly.</p>
        <div className="flex gap-3">
          <input
            type="email"
            placeholder="Enter test email address..."
            id="test-email-input"
            className="flex-1 h-10 px-4 bg-white border border-yellow-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
          />
          <button
            onClick={async () => {
              const testEmail = document.getElementById('test-email-input').value;
              if (!testEmail) {
                toast.error('Please enter an email address');
                return;
              }
              const token = localStorage.getItem('admin_token');
              try {
                await axios.post(`${API}/emails/test?to_email=${encodeURIComponent(testEmail)}`, {}, {
                  headers: { Authorization: `Bearer ${token}` }
                });
                toast.success(`Test email sent to ${testEmail}! Check your inbox.`);
                fetchEmailLogs(testEmail);
              } catch (error) {
                toast.error(error.response?.data?.detail || 'Failed to send test email');
              }
            }}
            className="px-6 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-all font-semibold"
          >
            Send Test Email
          </button>
        </div>
      </div>

      {/* Email Logs Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-200">
              <th className="text-left py-3 px-4 text-sm font-semibold text-[#0A0A0A]">To</th>
              <th className="text-left py-3 px-4 text-sm font-semibold text-[#0A0A0A]">Subject</th>
              <th className="text-left py-3 px-4 text-sm font-semibold text-[#0A0A0A]">Type</th>
              <th className="text-left py-3 px-4 text-sm font-semibold text-[#0A0A0A]">Status</th>
              <th className="text-left py-3 px-4 text-sm font-semibold text-[#0A0A0A]">Sent At</th>
              <th className="text-left py-3 px-4 text-sm font-semibold text-[#0A0A0A]">Actions</th>
            </tr>
          </thead>
          <tbody>
            {emailLogs.map((log, index) => (
              <tr key={index} className="border-b border-gray-100 hover:bg-gray-50">
                <td className="py-3 px-4 text-sm">{log.to}</td>
                <td className="py-3 px-4 text-sm max-w-xs truncate" title={log.subject}>{log.subject}</td>
                <td className="py-3 px-4">
                  <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${
                    log.email_type === 'welcome_guide' ? 'bg-blue-100 text-blue-800' :
                    log.email_type === 'appointment_confirmation' ? 'bg-green-100 text-green-800' :
                    log.email_type === 'post_call' ? 'bg-emerald-100 text-emerald-800' :
                    log.email_type === 'payment_chase_day3' ? 'bg-orange-100 text-orange-800' :
                    log.email_type === 'payment_chase_day7' ? 'bg-red-100 text-red-800' :
                    log.email_type === 'survey_reminder' ? 'bg-purple-100 text-purple-800' :
                    log.email_type === 'admin_appointment_reminder' ? 'bg-indigo-100 text-indigo-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {log.email_type || 'general'}
                  </span>
                </td>
                <td className="py-3 px-4">
                  <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${
                    log.status === 'sent' ? 'bg-[#25D366] text-white' : 'bg-red-500 text-white'
                  }`}>
                    {log.status}
                  </span>
                </td>
                <td className="py-3 px-4 text-sm text-[#525252]">
                  {log.sent_at ? format(new Date(log.sent_at), 'MMM d, yyyy h:mm a') : '-'}
                </td>
                <td className="py-3 px-4">
                  {log.html_content ? (
                    <button
                      onClick={() => handleViewEmail(log)}
                      className="flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-all text-xs font-semibold"
                    >
                      <Eye className="w-3 h-3" />
                      View
                    </button>
                  ) : (
                    <span className="text-xs text-gray-400">No preview</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {emailLogs.length === 0 && (
          <p className="text-center text-[#525252] py-8">
            {emailSearchQuery ? `No emails found for ${emailSearchQuery}` : 'Enter an email address to search logs'}
          </p>
        )}
      </div>
    </div>
  );
};

export default EmailLogsTab;
